import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { acceptWhatNextSuggestion, getProjectWhatNext } from '../../services/api'
import type { StrategicCategory } from '../../types'

/**
 * "What next · strategic suggestions" (figure S3) — the two or three things
 * worth doing on this invention that are not the pipeline's own next step.
 *
 * Every row is a suggestion the engine produced for THIS project, and "Create
 * todo" is the real accept call — it writes a todo the user will find in Tasks,
 * not a local tick. A category the accept API has no action for shows the
 * suggestion without a button rather than a button that fails on click.
 */
const ACTION_TYPE: Partial<Record<StrategicCategory, 'request_review' | 'plan_interviews' | 'search_grants' | 'start_patent_filing'>> = {
  expert_review: 'request_review',
  end_user_validation: 'plan_interviews',
  grant_alerts: 'search_grants',
  ip_protection: 'start_patent_filing',
}

export interface WhatNextSuggestionsProps {
  projectId: string
}

export default function WhatNextSuggestions({ projectId }: WhatNextSuggestionsProps) {
  const queryClient = useQueryClient()
  const [accepted, setAccepted] = useState<Record<string, string>>({})
  const [error, setError] = useState('')

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['project-whatnext', projectId],
    queryFn: () => getProjectWhatNext(projectId),
    enabled: !!projectId,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: ({ id, category }: { id: string; category: StrategicCategory }) =>
      acceptWhatNextSuggestion(projectId, id, ACTION_TYPE[category]!),
    onSuccess: (_result, variables) => {
      setAccepted((a) => ({ ...a, [variables.id]: 'Todo created' }))
      queryClient.invalidateQueries({ queryKey: ['crm-todos'] })
    },
    onError: () => setError('That suggestion could not be turned into a todo. Try again.'),
  })

  const rows = (data?.suggestions ?? []).filter((s) => s.status !== 'dismissed')

  return (
    <section data-part="WhatNextSuggestions" className="light-card p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-[13px] font-bold text-iflow-900">
          💡 What next <span className="font-normal text-gray-500">· strategic suggestions</span>
        </h3>
        <button
          type="button"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['project-whatnext', projectId] })}
          disabled={isFetching}
          className="rounded-lg border border-sky-200 px-2.5 py-1 text-[12px] font-semibold text-iflow-700 hover:bg-sky-50 disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh suggestions'}
        </button>
      </div>

      {isLoading && <p className="text-[12px] text-gray-500">Reading this invention’s suggestions…</p>}
      {isError && (
        <p className="text-[12px] text-gray-500">
          The suggestions could not be read just now. Nothing is missing from your invention — this
          panel failed to load.
        </p>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-[12px] text-gray-500">
          No strategic suggestions yet. They appear once this invention has been analysed.
        </p>
      )}

      {rows.map((s) => (
        <div key={s.suggestion_id} className="flex items-start gap-3 border-t border-sky-100 py-2 first-of-type:border-t-0">
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-semibold text-iflow-900">{s.title}</p>
            <p className="text-[11.5px] text-gray-500">{s.description || s.why_this_matters}</p>
          </div>
          {accepted[s.suggestion_id] ? (
            <span className="shrink-0 text-[11.5px] font-semibold text-emerald-600">✓ {accepted[s.suggestion_id]}</span>
          ) : ACTION_TYPE[s.category] ? (
            <button
              type="button"
              onClick={() => accept.mutate({ id: s.suggestion_id, category: s.category })}
              disabled={accept.isPending}
              className="shrink-0 rounded-lg border border-sky-200 px-2.5 py-1 text-[12px] font-semibold text-iflow-700 hover:bg-sky-50 disabled:opacity-50"
            >
              Create todo
            </button>
          ) : null}
        </div>
      ))}

      {error && <p className="mt-2 text-[11.5px] text-red-600">{error}</p>}
    </section>
  )
}
